import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import StatCard from '../components/ui/StatCard';
import FeedItem from '../components/ui/FeedItem';
import SocialCardModal from '../components/SocialCardModal';
import { getActivity } from '../services/creatorService';
import useMediaQuery from '../hooks/useMediaQuery';

const ACTIVITY_LIMIT = 10;

function formatNumber(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function formatCurrency(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
}

function formatSince(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

export default function DashboardPage() {
  const { creator } = useAuth();
  const { getCachedProfile, getCachedStats, getCachedActivity, getStale } = useData();
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Initialize with cached data for instant render
  const [profile, setProfile] = useState(() => getStale('profile'));
  const [stats, setStats] = useState(() => getStale('stats'));
  const [activity, setActivity] = useState(() => {
    const d = getStale('activity');
    return d?.activities || d?.items || (Array.isArray(d) ? d : []);
  });
  const [loading, setLoading] = useState(!getStale('profile'));
  const [error, setError] = useState(null);
  const [activityOffset, setActivityOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [socialCardOpen, setSocialCardOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setError(null);
        const [p, s, a] = await Promise.all([
          getCachedProfile(),
          getCachedStats(),
          getCachedActivity(),
        ]);
        if (!mounted) return;
        setProfile(p);
        setStats(s);
        const items = a?.activities || a?.items || (Array.isArray(a) ? a : []);
        setActivity(items);
        setActivityOffset(items.length);
        setHasMore(items.length === ACTIVITY_LIMIT);
      } catch {
        if (mounted) setError('Failed to load dashboard data.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [getCachedProfile, getCachedStats, getCachedActivity]);

  const loadMoreActivity = async () => {
    setLoadingMore(true);
    try {
      const res = await getActivity(ACTIVITY_LIMIT, activityOffset);
      const newItems = res.data?.activities || res.data?.items || (Array.isArray(res.data) ? res.data : []);
      setActivity(prev => [...prev, ...newItems]);
      setActivityOffset(prev => prev + newItems.length);
      setHasMore(newItems.length === ACTIVITY_LIMIT);
    } catch {
      // silently fail load more
    } finally {
      setLoadingMore(false);
    }
  };

  // Show skeleton only on very first load with no cached data
  if (loading && !profile) return <div className="loading-state">Loading...</div>;
  if (error && !profile) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const p = profile || {};
  const s = stats || {};
  const pfpUrl = p.twitter?.profileImageUrl || creator?.profileImageUrl;
  const handle = p.twitter?.username || creator?.twitterUsername;
  const followers = p.twitter?.followers;
  const displayName = p.name || creator?.name || 'Creator';
  const profileInitial = displayName.trim().charAt(0).toUpperCase();
  const sinceDate = p.bricktocreatorSince || p.createdAt;
  const totalCampaigns = p.workload?.totalCampaigns ?? s.totalCampaigns;
  const nftHolderVerified = !!p.wallet?.nftHolderVerified;

  return (
    <>
      <div className="profile-header" style={isMobile ? { flexDirection: 'column', alignItems: 'flex-start', gap: 22, padding: 24 } : {}}>
        <div className="profile-pfp">
          {pfpUrl ? (
            <img
              src={pfpUrl}
              alt="Profile"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : handle ? (
            <img
              src={`https://unavatar.io/twitter/${handle}`}
              alt="Profile"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <span>{profileInitial}</span>
          )}
        </div>
        <div className="profile-info" style={{ width: '100%' }}>
          <div className="profile-name">{displayName}</div>
          <div
            className="profile-handle"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}
          >
            <span>
              {handle ? `@${handle}` : ''}
              {followers ? ` · ${formatNumber(followers)} followers` : ''}
            </span>
            <button
              type="button"
              onClick={() => setSocialCardOpen(true)}
              style={{
                background: '#E4CA00',
                color: '#000',
                border: 'none',
                padding: '8px 14px',
                borderRadius: 4,
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 800,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                marginLeft: isMobile ? 0 : 'auto'
              }}
            >
              Generate Social Card
            </button>
          </div>
          <div className="profile-badges">
            {p.isBricktocreator && (
              <div className="profile-badge gold">Verified Creator</div>
            )}
            {sinceDate && (
              <div className="profile-badge">Since {formatSince(sinceDate)}</div>
            )}
            {totalCampaigns != null && (
              <div className="profile-badge">{totalCampaigns} Campaigns</div>
            )}
          </div>
        </div>
      </div>

      <div className="stats">
        <StatCard
          label="Total Earned"
          value={formatCurrency(s.totalEarned)}
          sub={s.completedCampaigns != null ? `${s.completedCampaigns} campaigns completed` : undefined}
          primary
        />
        <StatCard
          label="Active Campaigns"
          value={s.activeCampaigns ?? '—'}
          sub={s.dueSoon != null ? `${s.dueSoon} posts due this week` : undefined}
        />
        <StatCard
          label="Total Impressions"
          value={formatNumber(s.totalImpressions)}
          sub="Across all campaigns"
        />
        <StatCard
          label="Pending Payment"
          value={formatCurrency(s.pendingPayment)}
          sub={s.unpaidCampaigns != null ? `${s.unpaidCampaigns} campaigns unpaid` : undefined}
        />
      </div>

      <div className="sec-head">Recent Activity</div>
      <div className="feed">
        {activity.length === 0 ? (
          <div className="feed-item">
            <div className="feed-body">
              <div className="feed-text" style={{ color: '#555' }}>No recent activity.</div>
            </div>
          </div>
        ) : (
          activity.map((item, i) => (
            <FeedItem
              key={i}
              type={item.type}
              text={item.text || item.message}
              time={item.timestamp ? relativeTime(item.timestamp) : item.createdAt ? relativeTime(item.createdAt) : item.time || ''}
            />
          ))
        )}
        {hasMore && (
          <div style={{ textAlign: 'center', padding: '16px 20px', borderTop: '1px solid var(--line)' }}>
            <button
              onClick={loadMoreActivity}
              disabled={loadingMore}
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', padding: '10px 24px', background: 'var(--card2)', border: '1px solid var(--line2)', color: loadingMore ? '#333' : 'var(--gray)', cursor: loadingMore ? 'not-allowed' : 'pointer' }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>

      <SocialCardModal
        open={socialCardOpen}
        onClose={() => setSocialCardOpen(false)}
        totalEarned={s.totalEarned}
        handle={handle}
        nftHolderVerified={nftHolderVerified}
      />
    </>
  );
}
